import { useCallback, useMemo } from 'react';

import { Actions } from '../../../constants/actionTypes';
import { findDefaultWebclientConfig } from '../../../helpers/webclientHelper';
import { useMainDispatch } from '../../useMainDispatch';
import { useMainSelector } from '../../useMainSelector';

export const useWebclient = () => {
  const {
    settings: { webclient },
  } = useMainSelector();

  const dispatch = useMainDispatch();

  // sorted configs with default first
  const sortedConfigs = useMemo(() => {
    return [...webclient.configs].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return 0;
    });
  }, [webclient.configs]);

  const setSelectedWebclientConfig = useCallback(
    (configId: string) => {
      dispatch({
        type: Actions.SET_SETTINGS,
        payload: { webclient: { ...webclient, selectedConfigId: configId } },
      });
    },
    [webclient, dispatch],
  );

  const selectedWebclientConfig = useMemo(
    () =>
      webclient.configs.find(
        ({ identifier }) => identifier === webclient.selectedConfigId,
      ),
    [webclient.configs, webclient.selectedConfigId],
  );

  const defaultWebclientConfig = useMemo(
    () =>
      findDefaultWebclientConfig(webclient.configs) ?? {
        identifier: 'default',
        config: null,
      },
    [webclient.configs],
  );

  const setSelectedWhitelistedDomain = useCallback(
    (selectedWhitelistedDomain: string) => {
      dispatch({
        type: Actions.SET_SETTINGS,
        payload: { webclient: { ...webclient, selectedWhitelistedDomain } },
      });
    },
    [webclient, dispatch],
  );

  return {
    webclient,
    selectedWebclientConfig,
    setSelectedWebclientConfig,
    setSelectedWhitelistedDomain,
    defaultWebclientConfig,
    sortedConfigs,
    isLoadingConfigs: webclient.isLoadingConfigs,
  };
};
